"use client"

import { useEffect, useState } from "react"
import {
  Plus,
  Edit2,
  Trash2,
  Check,
  X,
  GraduationCap,
  Sparkles,
} from "lucide-react"
import {
  createEducation,
  getEducations,
  updateEducation,
  deleteEducation as apiDeleteEducation,
} from "../utils/api"

export default function Education() {
  const [educations, setEducations] = useState([])
  const [newEducation, setNewEducation] = useState("")
  const [editingId, setEditingId] = useState(null)
  const [editingName, setEditingName] = useState("")
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)
  const [savingId, setSavingId] = useState(null)
  const [deletingId, setDeletingId] = useState(null)

  useEffect(() => {
    const fetchEducations = async () => {
      try {
        setIsLoading(true)
        const response = await getEducations()
        const mapped = response.data.map((e) => ({
          id: e._id || e.id,
          name: e.name,
        }))
        setEducations(mapped)
      } catch (error) {
        console.error("Error fetching education list:", error)
        setError("Failed to load education entries.")
      } finally {
        setIsLoading(false)
      }
    }

    fetchEducations()
  }, [])

  const addEducation = async () => {
    if (!newEducation.trim()) return
    try {
      setSavingId("new")
      const response = await createEducation({ name: newEducation.trim() })
      const newItem = { id: response.data._id, name: response.data.name }
      setEducations((prev) => [...prev, newItem])
      setNewEducation("")
    } catch (error) {
      console.error("Error adding education:", error)
      setError("Failed to add education.")
    } finally {
      setSavingId(null)
    }
  }

  const deleteEducation = async (id) => {
    try {
      setDeletingId(id)
      await apiDeleteEducation(id)
      setEducations((prev) => prev.filter((e) => e.id !== id))
    } catch (error) {
      console.error("Error deleting education:", error)
      setError("Failed to delete education.")
    } finally {
      setDeletingId(null)
    }
  }

  const startEditing = (edu) => {
    setEditingId(edu.id)
    setEditingName(edu.name)
  }

  const saveEdit = async () => {
    if (!editingName.trim()) return
    try {
      setSavingId(editingId)
      const response = await updateEducation(editingId, {
        name: editingName.trim(),
      })
      setEducations((prev) =>
        prev.map((e) =>
          e.id === editingId ? { ...e, name: response.data.name } : e
        )
      )
      cancelEdit()
    } catch (error) {
      console.error("Error updating education:", error)
      setError("Failed to update education.")
    } finally {
      setSavingId(null)
    }
  }

  const cancelEdit = () => {
    setEditingId(null)
    setEditingName("")
  }

  const handleKeyPress = (e, action) => {
    if (e.key === "Enter") action()
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
          <p className="text-lg font-semibold text-gray-700">Loading education entries...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-purple-50 py-8 px-4">
      <div className="w-full mx-auto space-y-8">
        {/* Header */}
        <div className="text-center">
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="p-3 bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl shadow-lg">
              <GraduationCap className="h-8 w-8 text-white" />
            </div>
            <h1 className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              Education Management
            </h1>
          </div>
          <p className="text-gray-600 text-lg">Manage education levels for user profiles</p>
        </div>

        {/* Error */}
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-6 py-4 rounded-xl shadow-md flex items-center justify-between">
            <div className="flex items-center gap-2">
              <X className="w-5 h-5" />
              <strong className="font-bold">Error: </strong>
              <span>{error}</span>
            </div>
            <button
              onClick={() => setError(null)}
              className="text-sm font-medium underline hover:text-red-900"
            >
              Dismiss
            </button>
          </div>
        )}

        {/* Add Education */}
        <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
          <div className="bg-gradient-to-r from-green-600 to-teal-600 p-6 text-white">
            <div className="flex items-center gap-2">
              <Plus className="h-6 w-6" />
              <h2 className="text-xl font-semibold">Add New Education</h2>
            </div>
          </div>
          <div className="p-6">
            <div className="flex gap-4">
              <div className="flex-1">
                <input
                  type="text"
                  placeholder="Enter education level..."
                  value={newEducation}
                  onChange={(e) => setNewEducation(e.target.value)}
                  onKeyPress={(e) => handleKeyPress(e, addEducation)}
                  className="w-full h-12 px-4 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent text-lg"
                  disabled={savingId === "new"}
                />
              </div>
              <button
                onClick={addEducation}
                disabled={!newEducation.trim() || savingId === "new"}
                className="h-12 px-8 bg-gradient-to-r from-green-600 to-teal-600 hover:from-green-700 hover:to-teal-700 text-white rounded-xl font-semibold disabled:from-gray-400 disabled:to-gray-400 disabled:cursor-not-allowed flex items-center gap-2 shadow-lg hover:shadow-xl transition-all duration-200"
              >
                {savingId === "new" ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Adding...
                  </>
                ) : (
                  <>
                    <Plus className="w-5 h-5" />
                    Add
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* List */}
        <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
          <div className="bg-gradient-to-r from-orange-600 to-red-600 p-6 text-white">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Sparkles className="h-6 w-6" />
                <h2 className="text-xl font-semibold">Education List</h2>
              </div>
              <div className="bg-white/20 px-4 py-2 rounded-full">
                <span className="font-semibold">
                  {educations.length} {educations.length === 1 ? "entry" : "entries"}
                </span>
              </div>
            </div>
          </div>
          <div className="p-6 space-y-4">
            {educations.length === 0 ? (
              <div className="text-center py-16">
                <div className="w-24 h-24 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <GraduationCap className="w-12 h-12 text-orange-400" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">No Education Entries Yet</h3>
                <p className="text-gray-600">Start by adding your first education above!</p>
              </div>
            ) : (
              educations.map((edu, index) => (
                <div
                  key={edu.id}
                  className="group bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-xl p-4 transition-all duration-200 hover:shadow-md"
                >
                  {editingId === edu.id ? (
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 flex items-center justify-center bg-orange-100 rounded-lg">
                        <span className="text-orange-600 font-bold text-sm">{String(index + 1).padStart(2, "0")}</span>
                      </div>
                      <input
                        value={editingName}
                        onChange={(e) => setEditingName(e.target.value)}
                        onKeyPress={(e) => handleKeyPress(e, saveEdit)}
                        autoFocus
                        className="flex-1 h-10 px-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                        disabled={savingId === edu.id}
                      />
                      <div className="flex gap-2">
                        <button
                          onClick={saveEdit}
                          disabled={!editingName.trim() || savingId === edu.id}
                          className="h-10 px-4 bg-gradient-to-r from-green-600 to-teal-600 text-white rounded-lg font-medium flex items-center gap-2 shadow-md hover:shadow-lg"
                        >
                          {savingId === edu.id ? (
                            <>
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                              Saving...
                            </>
                          ) : (
                            <>
                              <Check className="w-4 h-4" />
                              Save
                            </>
                          )}
                        </button>
                        <button
                          onClick={cancelEdit}
                          className="h-10 px-4 bg-gradient-to-r from-gray-500 to-gray-600 text-white rounded-lg font-medium flex items-center gap-2 shadow-md hover:shadow-lg"
                        >
                          <X className="w-4 h-4" />
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 flex items-center justify-center bg-orange-100 rounded-lg">
                          <span className="text-orange-600 font-bold text-sm">{String(index + 1).padStart(2, "0")}</span>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="w-6 h-6 bg-gradient-to-r from-orange-500 to-red-500 rounded-full flex items-center justify-center">
                            <GraduationCap className="w-3 h-3 text-white" />
                          </div>
                          <span className="text-lg font-semibold text-gray-900">{edu.name}</span>
                        </div>
                      </div>
                      <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          onClick={() => startEditing(edu)}
                          className="h-10 px-4 bg-gradient-to-r from-yellow-500 to-orange-500 text-white rounded-lg font-medium flex items-center gap-2 shadow-md hover:shadow-lg"
                        >
                          <Edit2 className="w-4 h-4" />
                          Edit
                        </button>
                        <button
                          onClick={() => deleteEducation(edu.id)}
                          disabled={deletingId === edu.id}
                          className="h-10 px-4 bg-gradient-to-r from-red-500 to-rose-500 text-white rounded-lg font-medium flex items-center gap-2 shadow-md hover:shadow-lg"
                        >
                          {deletingId === edu.id ? (
                            <>
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                              Deleting...
                            </>
                          ) : (
                            <>
                              <Trash2 className="w-4 h-4" />
                              Delete
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  )
}